import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "../styles/Auth.css";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import bgVideo from "../assets/Loginbg-video.mp4";
import rghLogo from "../assets/RHGLandingPage.png";
import thinkImg from "../assets/think.png";
import { loginUser, googleSignIn, registerUser } from "../ApiService";

const TypingText = ({ text, speed = 100, start = true, onComplete, className }) => {
  const [displayedText, setDisplayedText] = useState("");
  const indexRef = useRef(0);
  const intervalRef = useRef(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!start || !text || hasStarted.current) return; // prevent rerun
    hasStarted.current = true;

    setDisplayedText("");
    indexRef.current = 0;

    intervalRef.current = setInterval(() => {
      if (indexRef.current < text.length) {
        indexRef.current += 1;
        setDisplayedText(text.substring(0, indexRef.current));
      } else {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        if (onComplete) onComplete();
      }
    }, speed);

    return () => clearInterval(intervalRef.current);
  }, [start, text, speed, onComplete]);

  return <span className={className}>{displayedText}</span>;
};
const Login = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [h2Done, setH2Done] = useState(false);
  const [h1FirstLineDone, setH1FirstLineDone] = useState(false);

  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [showPasswordReg, setShowPasswordReg] = useState(false);
  const [showConfirmPasswordReg, setShowConfirmPasswordReg] = useState(false);

  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const passwordRules = (pwd) => ({
    length: pwd.length >= 8 && pwd.length <= 30,
    uppercase: /[A-Z]/.test(pwd),
    lowercase: /[a-z]/.test(pwd),
    number: /[0-9]/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd),
  });

  const pwdChecks = passwordRules(registerData.password);
  const isPasswordValid = Object.values(pwdChecks).every(Boolean);
  const isConfirmValid =
    registerData.confirmPassword.length > 0 &&
    registerData.password === registerData.confirmPassword;
  const isRegisterValid =
    registerData.fullName.trim().length > 0 &&
    registerData.email.trim().length > 0 &&
    isPasswordValid &&
    isConfirmValid;

  // JWT save
  const saveAuthData = (token) => {
    localStorage.setItem("authToken", token);
    try {
      const decoded = jwtDecode(token);
      localStorage.setItem("userRole", "User");
      const User =
        decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] ||
        decoded.User;
      if (User) localStorage.setItem("User", User);
    } catch (err) {
      console.error("JWT decode failed:", err);
    }
  };

  // Login Handlers
  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await loginUser(loginData);
      if (res?.token) {
        saveAuthData(res.token);
      }
      // toast.success("Login successful 🎉", {
      //   autoClose: 100,
      //   onClose: () => navigate("/landingPage"),
      // });
      toast.success("Login successful 🎉");
      navigate("/landingPage");
    } catch (err) {
      toast.error(err.message || "Login failed ❌");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      const idToken = credentialResponse.credential;
      const res = await googleSignIn(idToken, "User");
      toast.success(res.message || "Google Login Successful 🎉");
      if (res.token) {
        saveAuthData(res.token);
        setTimeout(() => navigate("/landingPage"), 150);
      }
    } catch (err) {
      toast.error(err.message || "Google login failed ❌");
    } finally {
      setLoading(false);
    }
  };

  // Register Handler
  const handleRegister = async (e) => {
    e.preventDefault();
    if (loading) return;

    setPasswordTouched(true);
    setConfirmTouched(true);

    if (!isPasswordValid) {
      toast.error("Please satisfy all password requirements.");
      return;
    }
    if (!isConfirmValid) {
      toast.error("Passwords do not match!");
      return;
    }

    setLoading(true);
    try {
      await registerUser({ ...registerData, role: "User" });
      toast.success("Registered successfully");
      setShowRegister(false);
      setShowLogin(true);
    } catch (err) {
      toast.error(err.message || "Registration failed ❌");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {!showLogin && !showRegister && (
        <div className="login-logo">
          <img src={rghLogo} alt="Logo" />
        </div>
      )}
      {/* Background Video */}
      <video autoPlay muted loop playsInline className="bg-video">
        <source src={bgVideo} type="video/mp4" />
      </video>

      {!showLogin && !showRegister && (
        <div className="center-content">

          <h2><TypingText
            text="INTRODUCING THE FUTURE TODAY"
            speed={80}
            start={true}
            onComplete={() => setH2Done(true)}
          /></h2>
          <h1> <TypingText
            text="Verified Net Zero Hotels"
            speed={80}
            start={h2Done}
            onComplete={() => setH1FirstLineDone(true)}
          /></h1>
          <h1><TypingText
            text="Self Assessment"
            speed={80}
            start={h1FirstLineDone}
          /></h1>


          <div className="btn-block">
            <button className="login-btn" onClick={() => setShowLogin(true)}>
              Login
            </button>
          </div>
        </div>
      )}



      {/* Login Modal */}
      {showLogin && (
        <div className="modal-overlay" onClick={() => setShowLogin(false)}>
          <div className="auth-container" onClick={(e) => e.stopPropagation()}>
            <div className="logo-container">
              <img src={rghLogo} alt="Logo" className="logo" />
            </div>
            <form className="auth-form" onSubmit={handleLogin}>
              <input
                type="email"
                placeholder="Email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required
              />
              <div className="password-wrapper">
                <input
                  type={showPasswordLogin ? "text" : "password"}
                  placeholder="Password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                />
                <button type="button" className="eye-btn" onClick={() => setShowPasswordLogin(!showPasswordLogin)}>
                  {showPasswordLogin ? <VisibilityOff /> : <Visibility />}
                </button>
              </div>
              <button type="submit" className="btn-primary" disabled={loading}>
                <b>Login</b>
              </button>
            </form>
            <br />
            <p className="register-link">
              Don’t have an account? <span onClick={() => { setShowLogin(false); setShowRegister(true); }}><a>Register</a></span>
            </p>
          </div>
        </div>
      )}

      {/* Register Modal */}
      {showRegister && (
        <div className="modal-overlay" onClick={() => setShowRegister(false)}>
          <div className="auth-container" onClick={(e) => e.stopPropagation()}>
            <div className="logo-container">
              <img src={rghLogo} alt="Logo" className="logo" />
            </div>
            <h2 className="title">Create a new account</h2>
            <form className="auth-form" onSubmit={handleRegister}>
              <input
                name="fullName"
                placeholder="Full Name"
                value={registerData.fullName}
                onChange={(e) => setRegisterData({ ...registerData, fullName: e.target.value })}
                required
              />
              <input
                name="email"
                type="email"
                placeholder="Email"
                value={registerData.email}
                onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                required
              />
              <div className="password-wrapper">
                <input
                  name="password"
                  type={showPasswordReg ? "text" : "password"}
                  placeholder="Password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  onBlur={() => setPasswordTouched(true)}
                  required
                />
                <button type="button" className="eye-btn" onClick={() => setShowPasswordReg(!showPasswordReg)}>
                  {showPasswordReg ? <VisibilityOff /> : <Visibility />}
                </button>
              </div>
              {(passwordTouched || registerData.password.length > 0) && (
                <ul className="password-rules">
                  <li className={pwdChecks.length ? "ok" : "fail"}>
                    8–30 characters
                  </li>
                  <li className={pwdChecks.uppercase ? "ok" : "fail"}>
                    At least one uppercase letter
                  </li>
                  <li className={pwdChecks.lowercase ? "ok" : "fail"}>
                    At least one lowercase letter
                  </li>
                  <li className={pwdChecks.number ? "ok" : "fail"}>
                    At least one number
                  </li>
                  <li className={pwdChecks.special ? "ok" : "fail"}>
                    At least one special character
                  </li>
                </ul>
              )}
              <div className="password-wrapper">
                <input
                  name="confirmPassword"
                  type={showConfirmPasswordReg ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                  onBlur={() => setConfirmTouched(true)}
                  required
                />
                <button type="button" className="eye-btn" onClick={() => setShowConfirmPasswordReg(!showConfirmPasswordReg)}>
                  {showConfirmPasswordReg ? <VisibilityOff /> : <Visibility />}
                </button>
              </div>
              {(confirmTouched || registerData.confirmPassword.length > 0) && !isConfirmValid && (
                <p className="field-error">Passwords do not match.</p>
              )}
              <button
                type="submit"
                className="btn-primary"
                disabled={loading || !isRegisterValid}
              >
                Register
              </button>
            </form>
            <p className="register-link">
              Already have an account? <span onClick={() => { setShowRegister(false); setShowLogin(true); }}><a>Login</a></span>
            </p>
          </div>
        </div>
      )}

      <ToastContainer />
      {!showLogin && !showRegister && (
        <div className="think-image">
          <img src={thinkImg} alt="Think" />
        </div>
      )}
    </div>


  );
};


export default Login;